import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const MAX = 100 * 1024 * 1024;

const uploadDirect = (sig, file, onProgress) => new Promise((resolve, reject) => {
  const fd = new FormData();
  Object.entries(sig.params).forEach(([k, v]) => fd.append(k, v));
  fd.append("file", file);
  const xhr = new XMLHttpRequest();
  xhr.open("POST", sig.upload_url);
  xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(Math.round((e.loaded / e.total) * 100));
  xhr.onload = () => { try { const j = JSON.parse(xhr.responseText); xhr.status < 300 ? resolve(j) : reject(new Error(j?.error?.message || "Upload failed")); } catch (_e) { reject(new Error("Upload failed")); } };
  xhr.onerror = () => reject(new Error("Network error during upload"));
  xhr.send(fd);
});

export const BeamClipUploader = ({ roomId, onSaved }) => {
  const [kind, setKind] = useState("highlight");
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [pct, setPct] = useState(null);
  const save = async () => {
    if (!file) { toast.error("Choose an audio or video file"); return; }
    if (file.size > MAX) { toast.error("File is larger than 100 MB. Trim it and try again."); return; }
    try {
      setPct(0);
      const { data: sig } = await api.post(`/live/rooms/${roomId}/clips/upload-signature`);
      const up = await uploadDirect(sig, file, setPct);
      await api.post(`/live/rooms/${roomId}/clips`, { kind, title, public_id: up.public_id });
      toast.success(kind === "replay" ? "Replay saved" : "Highlight saved");
      setFile(null); setTitle(""); onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Upload failed");
    } finally { setPct(null); }
  };
  return (
    <div className="space-y-2 p-3 rounded-xl bg-white/5 border border-white/10" data-testid="beam-clip-uploader">
      <p className="text-sm font-bold text-white">Add a replay or highlight</p>
      <div className="flex gap-2" role="radiogroup">
        {[["highlight", "✨ Highlight"], ["replay", "🎬 Full replay"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setKind(k)} className={`text-xs px-3 py-1.5 rounded-full border font-bold ${kind === k ? "bg-pink-500/30 border-pink-400 text-white" : "border-white/15 text-pink-100/70"}`} data-testid={`beam-clip-kind-${k}`}>{l}</button>
        ))}
      </div>
      <Input value={title} maxLength={100} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)" data-testid="beam-clip-title" />
      <input type="file" accept="audio/*,video/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="block w-full text-xs text-pink-100/80 file:mr-3 file:rounded-full file:border-0 file:bg-white/10 file:px-3 file:py-1.5 file:text-white" data-testid="beam-clip-file" />
      <p className="text-[11px] text-pink-100/50">Audio or video, up to 100 MB. Recorded on your phone or exported from your camera.</p>
      <Button onClick={save} disabled={pct !== null} className="w-full bg-gradient-to-r from-rose-500 to-pink-500 font-bold" data-testid="beam-clip-save">
        {pct !== null ? `Uploading… ${pct}%` : "Save to this Live Beam"}
      </Button>
    </div>
  );
};
